import {assertAndContinue} from 'Asserts';
import {makeReactContext} from 'lib/makeReactContext';

import {GetActionInfoFn} from './actions/createGetActionInfoFn';

export enum AnalysisFlowchartMode {
  /**
   * STATIC flowcharts hide execution statuses (running, failed, etc.)
   */
  STATIC,
  /**
   * INTERACTIVE flowcharts include execution statuses (running, failed, etc.)
   */
  INTERACTIVE,
}

/**
 * Global-level configuration and callbacks for the flowchart.
 */
export interface AnalysisFlowchartConfig {
  mode: AnalysisFlowchartMode;
  getActionInfo: GetActionInfoFn;
  /** Step IDs to visually highlight in the flowchart. Non-highlighted nodes are dimmed. */
  highlightedStepIds: ReadonlySet<string>;
  /** Clears the active highlights so all nodes return to normal appearance. */
  clearHighlights: () => void;
  /** The step ID whose side panel is currently open, or null if none. */
  selectedStepId: string | null;
}

const EMPTY_SET: ReadonlySet<string> = new Set();

export const DEFAULT_ANALYSIS_FLOWCHART_CONFIG: AnalysisFlowchartConfig = {
  mode: AnalysisFlowchartMode.STATIC,
  getActionInfo: () => ({enabled: false, message: null, doAction: null}),
  highlightedStepIds: EMPTY_SET,
  clearHighlights: () => {},
  selectedStepId: null,
};

/**
 * Provide configuration and callbacks for the flowchart, which shouldn't vary per-step or per-edge.
 *
 * Step- and edge-specific data belong in `AnalysisGraphNodeData` or `AnalysisGraphEdgeData`.
 *
 * Avoid passing the entire pipeline to individual step and edge components; it can be difficult to unit test
 * components which traverse the entire pipeline.
 */
export const AnalysisFlowchartContext = makeReactContext<AnalysisFlowchartConfig>(() => {
  assertAndContinue(false, 'Could not find flowchart context');
  return DEFAULT_ANALYSIS_FLOWCHART_CONFIG;
}, 'AnalysisFlowchartContext');

export function useAnalysisFlowchartContext(): AnalysisFlowchartConfig {
  return AnalysisFlowchartContext.useContext();
}
